import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Link2, Trash2, Copy, ExternalLink, Edit3, Check, X as XIcon } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const TZ_OPTIONS = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "America/Sao_Paulo",
  "Europe/London", "Europe/Berlin", "Europe/Paris", "Europe/Madrid", "Europe/Amsterdam",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo", "Asia/Shanghai",
  "Australia/Sydney", "Pacific/Auckland", "UTC",
];

const DAYS = [
  { key: "0", label: "Mon" }, { key: "1", label: "Tue" }, { key: "2", label: "Wed" },
  { key: "3", label: "Thu" }, { key: "4", label: "Fri" }, { key: "5", label: "Sat" }, { key: "6", label: "Sun" },
];

const emptyForm = () => ({
  name: "30-min Intro",
  slug: `intro-${Math.random().toString(36).slice(2, 6)}`,
  description: "",
  duration_min: 30,
  buffer_min: 15,
  timezone: (Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"),
  is_active: true,
  include_meet_link: false,
  availability: {
    "0": { enabled: true, start: "09:00", end: "17:00" },
    "1": { enabled: true, start: "09:00", end: "17:00" },
    "2": { enabled: true, start: "09:00", end: "17:00" },
    "3": { enabled: true, start: "09:00", end: "17:00" },
    "4": { enabled: true, start: "09:00", end: "17:00" },
    "5": { enabled: false, start: "09:00", end: "17:00" },
    "6": { enabled: false, start: "09:00", end: "17:00" },
  },
});

export default function BookableCalendarsPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/booking-calendars");
      setItems(data || []);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (cal) => {
    setEditingId(cal.id);
    setForm({
      name: cal.name, slug: cal.slug, description: cal.description || "",
      duration_min: cal.duration_min, buffer_min: cal.buffer_min ?? 0,
      timezone: cal.timezone || "UTC", is_active: cal.is_active !== false,
      include_meet_link: !!cal.include_meet_link,
      availability: cal.availability || emptyForm().availability,
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) await api.put(`/booking-calendars/${editingId}`, form);
      else await api.post("/booking-calendars", form);
      toast.success(editingId ? "Updated" : "Calendar created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (cal) => {
    if (!window.confirm(`Delete "${cal.name}"? Existing bookings won't be removed.`)) return;
    try {
      await api.delete(`/booking-calendars/${cal.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const publicUrl = (slug) => `${window.location.origin}/book/c/${slug}`;
  const copyLink = (slug) => {
    navigator.clipboard.writeText(publicUrl(slug));
    toast.success("Link copied");
  };

  return (
    <div className="brutalist-card p-6 mb-6" data-testid="bookable-calendars-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="section-label">Bookable calendars</div>
          <h2 className="font-heading font-bold text-2xl tracking-tight leading-none mt-1">Let leads book themselves</h2>
        </div>
        <button onClick={openNew} data-testid="new-bookable-cal" className="bg-[var(--peak-accent)] text-white text-xs font-bold uppercase tracking-widest px-3 py-2 border-2 border-[var(--peak-accent)] hover:bg-black hover:border-black inline-flex items-center gap-1">
          <Plus className="w-3 h-3" strokeWidth={2.5} /> New
        </button>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-[var(--peak-muted)]">Create your first bookable link — leads pick a slot and it lands in your calendar automatically.</p>
      ) : (
        <ul className="divide-y divide-[var(--peak-border)]">
          {items.map((cal) => (
            <li key={cal.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`cal-row-${cal.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold uppercase text-xs tracking-widest truncate">{cal.name}</span>
                  {!cal.is_active && <span className="text-[10px] font-mono bg-[var(--peak-muted)] text-white px-1.5 py-0.5">OFF</span>}
                  <span className="text-[10px] font-mono bg-[var(--peak-surface-alt)] border border-[var(--peak-border)] px-1.5 py-0.5">{cal.duration_min}m</span>
                  {cal.buffer_min > 0 && <span className="text-[10px] font-mono bg-[var(--peak-surface-alt)] border border-[var(--peak-border)] px-1.5 py-0.5">+{cal.buffer_min}m buffer</span>}
                  <span className="text-[10px] font-mono text-[var(--peak-muted)]">{cal.timezone}</span>
                </div>
                <div className="text-xs text-[var(--peak-muted)] mt-0.5 truncate flex items-center gap-1">
                  <Link2 className="w-3 h-3" /> {publicUrl(cal.slug)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => copyLink(cal.slug)} data-testid={`cal-copy-${cal.id}`} title="Copy link" className="border border-[var(--peak-ink)] hover:bg-black hover:text-white p-1.5">
                  <Copy className="w-3 h-3" />
                </button>
                <a href={publicUrl(cal.slug)} target="_blank" rel="noreferrer" title="Open" className="border border-[var(--peak-ink)] hover:bg-black hover:text-white p-1.5">
                  <ExternalLink className="w-3 h-3" />
                </a>
                <button onClick={() => openEdit(cal)} data-testid={`cal-edit-${cal.id}`} title="Edit" className="border border-[var(--peak-ink)] hover:bg-black hover:text-white p-1.5">
                  <Edit3 className="w-3 h-3" />
                </button>
                <button onClick={() => remove(cal)} data-testid={`cal-delete-${cal.id}`} title="Delete" className="border border-[var(--peak-ink)] hover:bg-[#E50000] hover:border-[#E50000] hover:text-white p-1.5">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none border-2 border-[var(--peak-ink)]">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-2xl tracking-tight">
              {editingId ? "Edit calendar" : "New bookable calendar"}
            </DialogTitle>
            <DialogDescription className="text-xs text-[var(--peak-muted)]">
              Configure the meeting length, buffer, weekly hours and timezone. Your public link becomes /book/c/{"{slug}"}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bcal-name" className="brutalist-input" />
              </Field>
              <Field label="Public slug">
                <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} data-testid="bcal-slug" className="brutalist-input" />
              </Field>
            </div>
            <Field label="Description (shown on booking page)">
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="bcal-desc" className="brutalist-input min-h-[60px]" />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Duration (min)">
                <input type="number" min={5} max={480} value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: Number(e.target.value) })} data-testid="bcal-duration" className="brutalist-input" />
              </Field>
              <Field label="Buffer (min)">
                <input type="number" min={0} max={240} value={form.buffer_min} onChange={(e) => setForm({ ...form, buffer_min: Number(e.target.value) })} data-testid="bcal-buffer" className="brutalist-input" />
              </Field>
              <Field label="Timezone">
                <select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} data-testid="bcal-tz" className="brutalist-input">
                  {TZ_OPTIONS.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </Field>
            </div>
            <div>
              <div className="section-label mb-2">Weekly availability</div>
              <div className="space-y-1.5">
                {DAYS.map((day) => {
                  const w = form.availability[day.key];
                  return (
                    <div key={day.key} className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, availability: { ...form.availability, [day.key]: { ...w, enabled: !w.enabled } } })}
                        data-testid={`bcal-day-${day.key}`}
                        className={`w-10 h-8 border-2 text-[10px] font-mono flex items-center justify-center ${w.enabled ? "bg-[var(--peak-ink)] text-white border-[var(--peak-ink)]" : "border-[var(--peak-border)] text-[var(--peak-muted)]"}`}
                      >
                        {w.enabled ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                      </button>
                      <span className="text-xs font-bold uppercase w-10">{day.label}</span>
                      <input
                        type="time"
                        value={w.start}
                        disabled={!w.enabled}
                        onChange={(e) => setForm({ ...form, availability: { ...form.availability, [day.key]: { ...w, start: e.target.value } } })}
                        className="brutalist-input py-1 disabled:opacity-40"
                      />
                      <span className="text-xs">to</span>
                      <input
                        type="time"
                        value={w.end}
                        disabled={!w.enabled}
                        onChange={(e) => setForm({ ...form, availability: { ...form.availability, [day.key]: { ...w, end: e.target.value } } })}
                        className="brutalist-input py-1 disabled:opacity-40"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="bcal-active" />
              Accepting bookings
            </label>
            <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
              <input type="checkbox" checked={form.include_meet_link} onChange={(e) => setForm({ ...form, include_meet_link: e.target.checked })} data-testid="bcal-meet" />
              Attach a Google Meet link to every booking
              <span className="normal-case text-[10px] font-mono text-[var(--peak-muted)] tracking-normal">(requires connected Google Calendar)</span>
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="border-2 border-[var(--peak-ink)] text-xs font-bold uppercase tracking-widest px-4 py-2 hover:bg-black hover:text-white">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="bcal-save" className="bg-[var(--peak-accent)] text-white text-xs font-bold uppercase tracking-widest px-4 py-2 border-2 border-[var(--peak-accent)] hover:bg-black hover:border-black disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="section-label mb-1">{label}</div>
      {children}
    </label>
  );
}
